/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { simpleFetch } from '../../restWrapper';
import en from '../../assets/json/en.json';
import zh from '../../assets/json/zh.json';
import cn from '../../assets/json/cn.json';

// i18n.js normalizes page keys in these shared objects at app startup.
// Resolve both shapes so rendering is independent of module load order.
export const paymeText = language => {
  const locale = { en, zh, cn }[language] || en;
  return (locale.ACCOUNTOVERVIEW || locale.AccountOverview).paymeReconciliation;
};

const reasonKeys = {
  paid_status_review: 'paidStatusReview', lookup_unavailable: 'legacy',
  request_mismatch: 'requestMismatch', amount_currency_mismatch: 'amountCurrencyMismatch',
  transaction_mismatch: 'transactionMismatch', verification_mismatch: 'verificationMismatch',
  payment_not_found: 'paymentNotFound', preview_changed: 'previewChanged',
  expiry_review: 'expiryReview', receipt_permission: 'receiptPermission', receipt_correction_pending: 'correctionReview', confirmation_required: 'reviewTitle', fee_pending: 'feePending', status_review: 'verificationMismatch',
};
const money = value => value == null ? '—' : `HK$${Number(value).toLocaleString('en-HK', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

export const isPaymeVerified = payment => payment.status === 'complete' && !payment.paymeSyncExhausted && payment.paymeVerification === 'verified' &&
  payment.paymeFeeStatus === 'confirmed' && !payment.paymeManualReview?.changes?.length &&
  !payment.receiptStale && payment.paymeReview?.status !== 'required' &&
  !['pending', 'dispatching', 'review'].includes(payment.paymeReceiptDelivery) &&
  (!payment.paymeReceiptCorrection || payment.paymeReceiptCorrection.status === 'completed');

export default class PaymeReconciliation extends Component {
  state = { syncing: false, message: '', failed: false, preview: null, receiptAction: 'keep', verification: null };

  generation = 0;

  componentWillUnmount() { this.generation++; }

  componentDidMount() {
    if (this.props.onCheckingChange) this.props.onCheckingChange(false, this.props.payment.id, undefined);
    if (this.props.payment.paymeReview?.status === 'required') this.sync();
  }

  componentDidUpdate(previous, previousState) {
    if (previous.payment.id !== this.props.payment.id) {
      this.generation++;
      this.setState({ syncing: false, message: '', failed: false, preview: null, receiptAction: 'keep', verification: null }, () => {
        if (this.props.payment.paymeReview?.status === 'required') this.sync();
      });
      return;
    }
    if (this.props.onCheckingChange && (previousState.syncing !== this.state.syncing || previousState.verification !== this.state.verification)) {
      this.props.onCheckingChange(this.state.syncing, this.props.payment.id, this.state.verification);
    }
    if (this.props.payment.paymeReview?.status === 'required' &&
        (previous.payment.paymeReview?.status !== 'required' ||
          previous.payment.paymeReview?.feeStatus !== this.props.payment.paymeReview.feeStatus)) {
      this.sync();
    }
  }

  request = async apply => {
    if (this.state.syncing || (apply && !this.state.preview?.canApply)) return;
    const id = this.props.payment.id;
    const version = this.generation;
    const text = this.text();
    const confirmation = this.state.preview?.confirmation;
    const receiptAction = this.state.receiptAction;
    this.setState({ syncing: true, message: '', failed: false, ...(!apply ? { preview: null, receiptAction: 'keep' } : {}) });
    try {
      const response = await simpleFetch(`/organizations/payments/${id}/sync-payme${apply ? '/confirm' : ''}`,
        'POST', apply ? { confirmation, receiptAction } : {});
      if (this.props.payment.id !== id || this.generation !== version) return;
      if (response.status !== 200) {
        const key = reasonKeys[response.body?.code];
        this.setState({ syncing: false, message: (key && text[key]) || text.failed, failed: true, verification: 'review',
          ...(apply ? { preview: null, receiptAction: 'keep' } : {}) });
        return;
      }
      if (!apply) {
        const preview = response.body;
        // Matching completed payments need no financial or receipt decision.
        if (preview.canApply && Array.isArray(preview.changes) && !preview.changes.length &&
            this.props.payment.status === 'complete' && this.props.payment.receiptUrl &&
            !this.props.payment.receiptStale && !preview.requiresCompletionReview && preview.action !== 'expire') {
          this.setState({ syncing: false, preview, receiptAction: 'keep' }, () => { if (this.props.payment.id === id && this.generation === version) this.confirm(); });
        } else {
          this.setState({ syncing: false, preview, receiptAction: 'keep', verification:
            preview.problem || !preview.canApply ? 'review' : preview.changes?.length ? 'differences' : 'matched' });
        }
      } else {
        const correction = response.body.receiptCorrectionStatus;
        const receiptStale = correction !== 'completed' && (response.body.receiptStale || this.props.payment.receiptStale ||
          (this.state.preview?.receiptWillBeStale && receiptAction === 'keep'));
        const result = response.body.status === 'expired' ? 'expired' :
          correction === 'review' || response.body.receiptStatus === 'review' ? 'receipt_review' :
            (correction && correction !== 'completed') || ['pending', 'dispatching'].includes(response.body.receiptStatus) ? 'receipt_pending' :
              receiptStale ? 'receipt_stale' : 'verified';
        const message = result === 'expired' ? text.expired : result === 'receipt_review' ? (correction ? text.correctionReview : text.receiptReview) :
          result === 'receipt_pending' ? (correction ? text.correctionPreparing : text.preparing) : result === 'receipt_stale' ? text.receiptStaleNotice :
            correction === 'completed' ?
              (['issue_email', 'reissue_email'].includes(response.body.receiptCorrectionAction) ? text.correctionEmailed : text.correctionUpdated) : text.checked;
        let refreshed;
        try { refreshed = await this.props.onRefresh(); } catch (_) { refreshed = false; }
        if (this.props.payment.id !== id || this.generation !== version) return;
        this.setState({ syncing: false, preview: null, verification: refreshed === false ? 'refresh_failed' : result,
          failed: refreshed === false, message: refreshed === false ? text.refreshFailed : message });
      }
    } catch (_) {
      if (this.props.payment.id === id && this.generation === version) {
        this.setState({ syncing: false, message: text.failed, failed: true, verification: 'review' });
      }
    }
  };

  sync = () => this.request(false);
  confirm = () => this.request(true);

  renderPreview(text) {
    const { preview, syncing } = this.state;
    if (!preview) return null;
    const stored = preview.stored || {};
    const provider = preview.provider || {};
    const diagnostic = preview.diagnostics || {};
    const reported = preview.reported || {};
    const gross = provider.gross ?? reported.gross ?? diagnostic.requestGross ?? diagnostic.transactionGross;
    const currency = reported.currency || diagnostic.requestCurrency || diagnostic.transactionCurrency;
    const rows = [
      [text.grossLabel, money(stored.gross), money(gross)],
      [text.currencyLabel, stored.currency?.toUpperCase() || '—', currency || '—'],
      [text.feeLabel, money(stored.fee), money(provider.fee ?? reported.fee)],
      [text.processingLabel, money(stored.processingFee), money(provider.fee ?? reported.fee)],
      [text.serviceLabel, money(stored.serviceFee), money((provider.fee ?? reported.fee) == null ? null : 0)],
      [text.netLabel, money(stored.net), money(provider.net ?? reported.net)],
    ];
    const completing = !!preview.requiresCompletionReview;
    const initialReceiptChoices = completing && !stored.hasReceipt;
    const expiring = preview.action === 'expire';
    if (completing) rows.unshift([text.statusLabel, text.pendingStatus, text.paidReviewStatus]);
    if (expiring) rows.unshift([text.statusLabel, text.pendingStatus, text.expired]);
    const problemKey = reasonKeys[preview.problem?.code];
    return <div className="AccountOverview-paymePreview" role="region" aria-label={text.reviewTitle}>
      <h3>{text.reviewTitle}</h3>
      <p>{text.previewReadOnly}</p>
      <table><thead><tr><th>{text.fieldLabel}</th><th>{text.storedLabel}</th><th>{preview.canApply ? text.providerLabel : text.providerReturnedLabel}</th></tr></thead>
        <tbody>{rows.map(([label, before, after]) => <tr key={label}>
          <th scope="row">{label}</th><td>{before}</td><td className={before !== after ? 'AccountOverview-paymeChanged' : ''}>{after}</td>
        </tr>)}</tbody></table>
      {(reported.status || reported.transactionId) && <details className="AccountOverview-paymeProviderDetails">
        <summary>{text.providerDetails}</summary><dl>
          <dt>{text.providerStatus}</dt><dd>{reported.status || '—'}</dd>
          <dt>{text.transactionId}</dt><dd>{reported.transactionId || '—'}</dd>
          <dt>{text.reportedFee}</dt><dd>{reported.feeAsReturned ?? '—'}</dd>
        </dl></details>}
      <p className="AccountOverview-paymeFootnote">{text.derivedNetNote}</p>
      {expiring && <p>{text.expiryWarning}</p>}
      {preview.reported?.status === 'PR007' && !expiring && !preview.problem && <p>{text.expiryWait}</p>}
      {problemKey && <p role="alert">{text[problemKey]}</p>}
      {preview.problem && diagnostic.amountEvidence?.length > 0 && <details>
        <summary>{text.verificationDetails}</summary>
        <ul>{diagnostic.amountEvidence.map(item => <li key={item.field}>
          {item.field}: {item.value || (item.keys?.length ? item.keys.join(', ') : text.pendingValue)} ({item.type})
        </li>)}</ul>
      </details>}
      {!preview.diagnostics?.settled && !preview.problem && <p>{text.notSettled}</p>}
      {preview.receiptWillBeStale && this.state.receiptAction === 'keep' && <p>{text.receiptCorrectionWarning}</p>}
      {preview.canApply && !expiring && stored.hasReceipt && preview.canReissueReceipt && <fieldset disabled={syncing}>
        <legend>{text.receiptChoice}</legend>
        {['keep', 'reissue', 'reissue_email'].map(action => <label key={action} className="AccountOverview-paymeReceiptChoice">
          <input type="radio" name={"payme-receipt-" + this.props.payment.id} value={action}
            checked={this.state.receiptAction === action}
            onChange={() => this.setState({ receiptAction: action })} />
          <span><strong>{text['choice_' + action]}</strong><small>{text['choiceHelp_' + action]}</small></span>
        </label>)}
        <p>{text.sameReceiptNumber}</p>
      </fieldset>}
      {preview.canApply && initialReceiptChoices && <fieldset disabled={syncing}>
        <legend>{text.receiptChoice}</legend>
        {['keep', 'issue', 'issue_email'].map(action => <label key={action} className="AccountOverview-paymeReceiptChoice">
          <input type="radio" name={"payme-receipt-" + this.props.payment.id} value={action}
            checked={this.state.receiptAction === action}
            onChange={() => this.setState({ receiptAction: action })} />
          <span><strong>{text['initialChoice_' + action]}</strong><small>{text['initialHelp_' + action]}</small></span>
        </label>)}
      </fieldset>}
      {preview.receiptMayBeIssued && !expiring && !initialReceiptChoices && <p>{text.receiptIssueWarning}</p>}
      {preview.canApply && !(preview.changes || []).length && <p>{text.noDifferences}</p>}
      <div className="AccountOverview-paymeActions">
        {preview.canApply && <button className="AccountOverview-paymePrimary" type="button" disabled={syncing} onClick={this.confirm}>
          {syncing ? text.applying : expiring ? text.confirmExpiry : completing ? text.confirmComplete : text.confirmChanges}
        </button>}
        <button type="button" disabled={syncing} onClick={() => this.setState({ preview: null, receiptAction: 'keep' })}>{text.cancel}</button>
      </div>
    </div>;
  }

  text() {
    return paymeText(this.props.language);
  }

  render() {
    const { payment } = this.props;
    if (payment.PaymentMode !== 'PayMe' || !['pending_for_confirmation', 'processing', 'complete', 'expired'].includes(payment.status)) return null;
    const text = this.text();
    if (payment.status === 'expired') return <section className="AccountOverview-paymeReconciliation" aria-label={text.heading}>
      <h2>{text.heading}</h2><p>{text.expired}</p><p>{text.expiredDescription}</p>
    </section>;
    const reviewRequired = payment.paymeReview?.status === 'required';
    const verified = payment.paymeVerification === 'verified';
    const confirmed = payment.paymeFeeStatus === 'confirmed';
    const historical = payment.status === 'complete' && !payment.paymeFeeStatus && !verified;
    const dispatching = payment.paymeReceiptDelivery === 'dispatching';
    const needsReview = payment.paymeReceiptDelivery === 'review' ||
      (payment.paymeSyncExhausted && payment.paymeReceiptDelivery === 'pending' && payment.paymeReceiptIssue) || (dispatching &&
      (!payment.paymeReceiptAttemptedAt || Date.now() - new Date(payment.paymeReceiptAttemptedAt).getTime() > 120000));
    const correction = payment.paymeReceiptCorrection;
    const correctionMessage = correction && (correction.status === 'review' ||
      (['processing', 'dispatching'].includes(correction.status) &&
        (!correction.updatedAt || Date.now() - new Date(correction.updatedAt).getTime() > 120000))
      ? text.correctionReview : correction.status !== 'completed' ? text.correctionPreparing :
        !payment.receiptStale ? (['issue_email', 'reissue_email'].includes(correction.action) ? text.correctionEmailed : text.correctionUpdated) : null);
    const status = reviewRequired ? text.paidReviewStatus : historical ? text.historical : !verified ? text.unverified : !confirmed ? text.feePending :
      needsReview ? text.receiptReview : (dispatching || payment.paymeReceiptDelivery === 'pending') ? text.preparing : text.confirmed;
    const compact = this.props.showSyncButton === false && payment.status === 'complete' && verified && confirmed &&
      !reviewRequired && !needsReview && !payment.paymeSyncExhausted && (!correction || correction.status === 'completed') && !payment.receiptStale &&
      !['pending', 'dispatching'].includes(payment.paymeReceiptDelivery);
    const savedDifferences = payment.paymeManualReview?.changes?.length > 0 || this.state.verification === 'differences';
    if (compact && !savedDifferences && !this.state.preview && !this.state.message) return null;
    return (
      <section className={`AccountOverview-paymeReconciliation${compact ? ' AccountOverview-paymeReconciliation--compact' : ''}`} aria-label={text.heading}>
        {!compact && <React.Fragment><div className="AccountOverview-paymeHeader">
          <div><h2>{text.heading}</h2><p className="AccountOverview-paymeStatus"><span aria-hidden="true" className={historical ? 'AccountOverview-paymeDot--historical' : verified && confirmed && !needsReview ? 'AccountOverview-paymeDot--confirmed' : ''} />{status}</p></div>
          {payment.canSyncPayme && this.props.showSyncButton !== false && <button type="button" disabled={this.state.syncing} onClick={this.sync}>
            {this.state.syncing ? text.checking : text.sync}
          </button>}
        </div>
        {this.props.showSyncButton === false && this.state.syncing && !this.props.onCheckingChange && <p role="status">{text.checking}</p>}
        {reviewRequired && <p role="status">{text.paidReviewNote}</p>}
        {correctionMessage && correctionMessage !== this.state.message && <p role="status">{correctionMessage}</p>}
        {verified && !confirmed && <p>{text.receiptPending}</p>}
        {payment.paymeReceiptDelivery === 'pending' && payment.paymeReceiptIssue && !payment.paymeSyncExhausted && <p role="status">{text.generationRetry}</p>}
        {payment.paymeSyncExhausted && (!reviewRequired || payment.paymeReview.feeStatus === 'pending') && <p role="status">{text.retryReview}</p>}
        {historical && <p className="AccountOverview-paymeFootnote">{text.historicalNote}</p>}
        {!historical && !payment.canSyncPayme && !verified && <p>{text.legacy}</p>}
        </React.Fragment>}
        {savedDifferences && !this.state.preview && !this.state.message && <p className="AccountOverview-paymeFootnote" role="status">{payment.canSyncPayme ? text.savedDifferences : text.savedDifferencesUnavailable}</p>}
        {this.renderPreview(text)}
        {this.state.message && <p role={this.state.failed ? 'alert' : 'status'}>{this.state.message}</p>}
      </section>
    );
  }
}
PaymeReconciliation.propTypes = {
  payment: PropTypes.object.isRequired,
  language: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  showSyncButton: PropTypes.bool,
  onCheckingChange: PropTypes.func,
};

// Gross comes from the original charge, independently of missing fee/net data.
export const paymeGross = payment => payment.paymeConfirmedGross != null
  ? Number(payment.paymeConfirmedGross)
  : Number(payment.baseAmount != null ? payment.baseAmount : payment.amount) +
    (payment.coverFee ? Number(payment.coverFeeAmount || 0) : 0);

export function PaymeSettlementRows({ payment, language = 'en' }) {
  const text = paymeText(language);
  const pending = payment.paymeFeeStatus === 'pending' ||
    (payment.status === 'pending_for_confirmation' && payment.paymeFeeStatus !== 'confirmed');
  return <React.Fragment>
    {[['fee', text.feeLabel], ['net', text.netLabel]].map(([key, label]) => (
      <tr key={key} className="AccountOverview-paymentRow">
        <th><div className="AccountOverview-description">{label}</div></th>
        <th><div className="AccountOverview-dataInput">
          {!pending && payment[key] != null
            ? `HK$${Number(payment[key]).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : pending ? text.pendingValue : '—'}
        </div></th>
      </tr>
    ))}
  </React.Fragment>;
}
PaymeSettlementRows.propTypes = { payment: PropTypes.object.isRequired, language: PropTypes.string };
